import session from '@updater-core/interactors/shell/session';
import eventTracker from '@updater-core/interactors/shell/tracker';
import routerInteractor from '@updater-core/interactors/shell/router';
import routerPersistance from '@updater-core/interactors/shell/router-persistance';
import siteBranding from '@updater-core/interactors/shell/site-branding';
import optimizely from '@updater-core/interactors/shell/optimizely';
import journey from '@updater-core/interactors/shell/journey';
import { redirectIntoQuickSetup } from '@updater-core/redirects/quickSetup';
import { createRouter } from './router';
import { buildDaisyChain } from './daisy-chain';
import { buildWebpushNotification } from './webpush';
import { buildMaintenance } from './maintenance';
import { buildHeader } from '../apps/header';
import { setupSegment } from './segment';

export function build() {
  const container = document.querySelector('upd-micro-app-container');
  const headerContainer = document.querySelector('.upd-micro-app-header');
  const maintenance = buildMaintenance();
  const daisyChain = buildDaisyChain({ interactors: { session } });

  siteBranding.actions.setInviteToken(getInviteToken());

  buildWebpushNotification({
    interactors: { session },
  });
  setupSegment({ interactors: { session } });

  siteBranding.connect(({ entityBrandings: { whiteLabel } = {} }) => {
    if (whiteLabel) {
      document.head.querySelector('title').innerText = whiteLabel.displayName;
      document.head.querySelector('link[rel="shortcut icon"]').href =
        whiteLabel.favicon;
    }
  });

  // This is here to make it easy for micro-apps that are following the Next
  // migration path to access top level shell interactors.
  window.interactors = {
    session,
    tracker: eventTracker,
    siteBranding,
    optimizely,
    journey,
    daisyChain,
    routerPersistance,
  };

  session.actions.init().then(async ({ token }) => {
    if (token) {
      const redirected = await redirectIntoQuickSetup();
      if (redirected) {
        return; // early return
      }
    }
    const interactors = {
      session,
      maintenance,
      eventTracker,
      daisyChain,
      siteBranding,
      optimizely,
      journey,
    };

    const internalRouter = createRouter({
      interactors,
      container,
    });

    internalRouter.actions = {
      go: internalRouter.go,
    };

    const router = {
      ...internalRouter, // legacy access to internal api
      ...routerInteractor,
    };

    const header = buildHeader({
      container: headerContainer,
      interactors: {
        siteBranding,
        router,
        journey,
        session,
      },
    });

    window.interactors = {
      ...window.interactors,
      router,
      header,
    };

    router.setInteractors((originalInteractors) => ({
      ...originalInteractors,
      header,
    }));

    router.actions.init({ router: internalRouter });

    router.init().then(() => {
      maintenance.connect((state) => {
        if (maintenance.selectors.underMaintenance(state)) {
          router.go('/maintenance');
        }
      });
    });

    journey.actions.hasNewHeaderPromise().then((enabled) => {
      if (enabled) {
        container.classList.add('new-header-feature-enabled');
      }
    });

    // For debugging purposes only
    container.setAttribute('debug', '');
    container.setAttribute('app-shell', '');
    container.debug = {
      router,
      ...interactors,
    };
  });
}

function getInviteToken() {
  const MOVER_APP_DATA_KEY = 'upd_mover';

  const pathParamInviteToken = (window.location.pathname.match(
    /(utilities-confirmation|invite)\/([0-9a-f-]{36})/
  ) || [])[2];
  if (pathParamInviteToken) {
    return pathParamInviteToken;
  }
  // return from storage if not found in route url
  try {
    const {
      signUp: { invite_token: inviteToken },
    } = JSON.parse(localStorage.getItem(MOVER_APP_DATA_KEY));
    if (inviteToken) {
      return inviteToken;
    }
  } catch (ex) {
    // maybe local storage doesnt have item, or item could't be parsed
    // so we know we do not have invite token in storage yet.
  }
  // return no invite token if none found
  return '';
}
